<template>
    <div class="thread-wrapper">
      <div class="thread-layout">
        <!-- Main Column -->
        <div class="main-column">
          <!-- Thread Header - Simplified with only title and pagination -->
          <div class="thread-header">
            <h1 class="typo-headline-4">{{ thread.title }}</h1>
            
            <!-- Thread Actions -->
            <div class="thread-actions">
              <!-- Favorite Button (Logged users only) -->
              <button 
                v-if="isLoggedIn"
                class="favorite-btn" 
                :class="{ 'is-favorite': isFavorite }"
                @click="toggleFavorite"
                :disabled="favoriteLoading"
              >
                <i class="material-icons">{{ isFavorite ? 'star' : 'star_border' }}</i>
                <span>{{ isFavorite ? 'Quitar de favoritos' : 'Añadir a favoritos' }}</span>
              </button>
              
              <!-- Negative Forum Button (Admin only) -->
              <button 
                v-if="isAdmin"
                class="negative-forum-btn" 
                :class="{ 'is-negative': isNegative }"
                @click="toggleNegativeForum"
                :disabled="negativeForumLoading"
              >
                <i class="material-icons">{{ isNegative ? 'visibility_off' : 'visibility' }}</i>
                <span>{{ isNegative ? 'Mostrar en contenido reciente' : 'Ocultar de contenido reciente' }}</span>
              </button>
            </div>
            
            <!-- Thread Pagination -->
            <div class="thread-pagination">
              <div class="pagination-controls">
                <button class="pagination-nav-btn" :disabled="commentsCurrentPage <= 1" @click="goToFirstPage">&lt;&lt;</button>
                <button class="pagination-nav-btn" :disabled="commentsCurrentPage <= 1" @click="prevCommentPage">&lt;</button>
                
                <div class="pagination-numbers">
                  <button 
                    v-for="page in displayedPageNumbers" 
                    :key="page" 
                    class="pagination-number-btn" 
                    :class="{ active: page === commentsCurrentPage }"
                    @click="goToPage(page)"
                  >
                    {{ page }}
                  </button>
                </div>
                
                <button class="pagination-nav-btn" :disabled="commentsCurrentPage >= totalCommentPages" @click="nextCommentPage">&gt;</button>
                <button class="pagination-nav-btn" :disabled="commentsCurrentPage >= totalCommentPages" @click="goToLastPage">&gt;&gt;</button>
              </div>

              <div class="thread-navigation-row">
                <!-- Go to First Message Button -->
                <button 
                  class="thread-nav-btn page-navigation-btn"
                  @click="goToFirstMessage"
                  title="Ir al primer mensaje del hilo"
                >
                  <i class="material-icons">first_page</i>
                  <span>Primer mensaje</span>
                </button>

                <!-- Scroll to Bottom Button -->
                <button 
                  class="scroll-to-bottom-btn page-navigation-btn"
                  @click="scrollToBottom"
                  title="Ir al final de la página"
                >
                  <i class="material-icons">keyboard_arrow_down</i>
                  <span>Final de la página</span>
                </button>

                <!-- Go to Last Message Button -->
                <button 
                  class="thread-nav-btn page-navigation-btn"
                  @click="goToLastMessage"
                  title="Ir al último mensaje del hilo"
                >
                  <i class="material-icons">last_page</i>
                  <span>Último mensaje</span>
                </button>
              </div>
            </div>
          </div>
  
          <!-- Thread Content -->
          <section class="thread-content">
            <!-- Loading and Error States -->
            <div v-if="loading" class="loading-message typo-body-2">
              Cargando comentarios...
            </div>
            <div v-else-if="error" class="error-message typo-body-2">
              Error al cargar el hilo.
            </div>
  
            <!-- Original Post and Comments List -->
            <div v-else>
              <!-- Stock Thread Header (if it's a stock thread) -->
              <stock-thread-header
                v-if="Boolean(thread.title) && isStockThread && commentsCurrentPage === 1"
                :threadId="$route.params.nid"
                :threadTitle="thread.title"
                class="stock-thread-header"
              />
  
              <!-- Original Post rendered as a ForumMessage (if it's not a stock thread) -->
              <forum-message
                v-if="!isStockThread && thread && commentsCurrentPage === 1"
                :comment="{
                  cid: 'op',
                  pid: '0',
                  nid: $route.params.nid,
                  name: thread.author,
                  created: thread.created,
                  comment_body_value: thread.body,
                  uid: thread.author_uid,
                  images: thread.images || []
                }"
                :format-timestamp="formatTimestamp"
                :is-thread-author="true"
                :is-first-page="commentsCurrentPage === 1"
                :comment-index="0"
                :user-karma="getUserKarma(thread.author_uid)"
                :is-admin="isAdmin"
                class="original-post"
                @quote-comment="handleQuoteComment"
                @reply-to-comment="handleReplyToComment"
                @goto-quoted-message="scrollToComment"
                @message-updated="handleOriginalPostUpdate"
              />
  
              <!-- Comments List -->
              <forum-message
                v-for="(comment, index) in paginatedComments"
                :key="comment.cid"
                :comment="comment"
                :parent-comment="getParentComment(comment)"
                :format-timestamp="formatTimestamp"
                :is-thread-author="comment.name === thread.author"
                :comment-index="getCommentStartIndex() + index"
                :user-karma="getUserKarma(comment.uid)"
                :is-admin="isAdmin"
                :id="`comment-${comment.cid}`"
                @quote-comment="handleQuoteComment"
                @reply-to-comment="handleReplyToComment"
                @goto-quoted-message="scrollToComment"
                @need-response-changed="handleNeedResponseChanged"
              />
  
              <!-- Bottom Pagination Controls - Updated to match top pagination -->
              <div class="thread-pagination bottom-pagination">
                <div class="pagination-controls">
                  <button class="pagination-nav-btn" :disabled="commentsCurrentPage <= 1" @click="goToFirstPage">&lt;&lt;</button>
                  <button class="pagination-nav-btn" :disabled="commentsCurrentPage <= 1" @click="prevCommentPage">&lt;</button>
                  
                  <div class="pagination-numbers">
                    <button 
                      v-for="pageNum in displayedPageNumbers" 
                      :key="pageNum" 
                      :class="['pagination-number-btn', { active: pageNum === commentsCurrentPage }]"
                      @click="goToPage(pageNum)"
                    >
                      {{ pageNum }}
                    </button>
                  </div>
                  
                  <button class="pagination-nav-btn" :disabled="commentsCurrentPage >= totalCommentPages" @click="nextCommentPage">&gt;</button>
                  <button class="pagination-nav-btn" :disabled="commentsCurrentPage >= totalCommentPages" @click="goToLastPage">&gt;&gt;</button>
                </div>

                <div class="thread-navigation-row">
                  <!-- Go to First Message Button -->
                  <button 
                    class="thread-nav-btn page-navigation-btn"
                    @click="goToFirstMessage"
                    title="Ir al primer mensaje del hilo"
                  >
                    <i class="material-icons">first_page</i>
                    <span>Primer mensaje</span>
                  </button>

                  <!-- Scroll to Top Button -->
                  <button 
                    class="pagination-nav-btn scroll-to-top-btn page-navigation-btn"
                    @click="scrollToTop"
                    title="Ir al inicio de la página"
                  >
                    <i class="material-icons">keyboard_arrow_up</i>
                    <span>Inicio de la página</span>
                  </button>

                  <!-- Go to Last Message Button -->
                  <button 
                    class="thread-nav-btn page-navigation-btn"
                    @click="goToLastMessage"
                    title="Ir al último mensaje del hilo"
                  >
                    <i class="material-icons">last_page</i>
                    <span>Último mensaje</span>
                  </button>
                </div>
              </div>
              
              <!-- Thread Reply Button -->
              <div class="thread-reply-container" v-if="canWrite">
                <button class="thread-reply-btn" @click="openReplyModal">
                  <i class="material-icons">reply</i>
                  Responder al hilo
                </button>
              </div>
            </div>
          </section>
        </div>
  
        <!-- Side Column -->
        <div class="side-column">
          <!-- Navigation Links -->
          <navigation-links 
            v-if="thread"
            :section-id="thread.forum_tid"
            :section-name="thread.forum_name"
            :section-slug="thread.forum_slug"
          />

          <!-- User Activity Widget -->
          <user-activity-widget />

          <!-- Related Forums -->
          <!-- <related-forums-container 
            v-if="thread && thread.forum_tid"
            :current-section-id="thread.forum_tid"
          /> -->

          <!-- Recent Content -->
          <recent-content-container />
          
          <!-- Trending Topics -->
          <!--<trending-topics-container />-->
  
          <!-- Forum Stats -->
          <!--<forum-stats-container />-->
        </div>
      </div>
      
      <!-- Reply Modal - Moved inside the main wrapper div -->
      <reply-modal
        :show="showReplyModal"
        :thread-id="$route.params.nid"
        :quoted-comment="quotedComment"
        :quoted-comment-number="quotedCommentNumber"
        @close="closeReplyModal"
        @reply-submitted="handleReplySubmitted"
      />
    </div>
  </template>
  
  <script>
  import forumApi from '@/services/forumApi';
  import { mapGetters } from 'vuex';
  import ForumMessage from './components/ForumMessage.vue';
  import TrendingTopicsContainer from '../components/TrendingTopicsContainer.vue';
  import RelatedForumsContainer from '../components/RelatedForumsContainer.vue';
  import StockThreadHeader from './components/StockThreadHeader.vue';
  import RecentContentContainer from '../components/RecentContentContainer.vue';
  import ForumStatsContainer from '../components/ForumStatsContainer.vue';
  import NavigationLinks from '../components/NavigationLinks.vue';
  import ReplyModal from './components/ReplyModal.vue';
  import UserActivityWidget from '../components/UserActivityWidget.vue';
  
  const axios = forumApi;
  
  export default {
    name: 'ForumThread',
    components: {
      ForumMessage,
      TrendingTopicsContainer,
      RelatedForumsContainer,
      StockThreadHeader,
      RecentContentContainer,
      ForumStatsContainer,
      NavigationLinks,
      ReplyModal,
      UserActivityWidget
    },
    data() {
      return {
        thread: {},
        comments: [],
        loading: false,
        error: null,
        commentsCurrentPage: 1,
        commentsPerPage: 10,
        maxPagesToShow: 5,
        // New data properties for reply functionality
        showReplyModal: false,
        quotedComment: null,
        quotedCommentNumber: null,
        // Add karma data storage
        userKarma: {},
        isFavorite: false,
        favoriteLoading: false,
        shouldScrollToLastComment: false,
        // Add negative forum flag status
        isNegative: false,
        negativeForumLoading: false
      };
    },
    computed: {
      ...mapGetters(['_g_IsUserLogged', '_g_GetUserRawData', '_g_User']),
      isLoggedIn() {
        return this._g_IsUserLogged;
      },
      isAdmin() {
        return !!(this._g_User && this._g_User.is_admin_user);
      },
      canWrite() {
        // Admins always can write. For regular users rely on forum writer flag exposed by backend in _g_User
        return this.isAdmin || !!(this._g_User && this._g_User.is_forum_writer_user);
      },
      // Determinar si es un hilo de tipo stock
      isStockThread() {
        return this.thread && (!this.thread.author || !this.thread.author_uid);
      },
      // Updated to remove sorting functionality
      sortedComments() {
        return this.comments.slice();
      },
      // Paginación: devuelve los comentarios correspondientes a la página actual
      paginatedComments() {
        const start = (this.commentsCurrentPage - 1) * this.commentsPerPage;
        return this.sortedComments.slice(start, start + this.commentsPerPage);
      },
      // Updated to ensure at least 1 page even when there are no comments
      totalCommentPages() {
        // Always return at least 1 page (for the original post or stock thread header)
        return Math.max(1, Math.ceil(this.sortedComments.length / this.commentsPerPage));
      },
      // Updated to handle the case when there are no pages
      displayedPageNumbers() {
        const totalPages = this.totalCommentPages;
        const currentPage = this.commentsCurrentPage;
        const maxPages = this.maxPagesToShow;
        
        // Always show at least one page
        if (totalPages < 1) {
          return [1];
        }
        
        if (totalPages <= maxPages) {
          // If there are fewer pages than the maximum to show, show all
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        
        // Calcular el rango de páginas a mostrar
        let startPage = Math.max(currentPage - Math.floor(maxPages / 2), 1);
        let endPage = startPage + maxPages - 1;
        
        // Ajustar si el rango excede el total de páginas
        if (endPage > totalPages) {
          endPage = totalPages;
          startPage = Math.max(endPage - maxPages + 1, 1);
        }
        
        return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
      }
    },
    mounted() {
      this.fetchThread().then(() => {
        // Verificar si debemos ir al último comentario después de cargar los datos
        if (this.$route.query.last === 'true') {
          this.$nextTick(() => {
            this.scrollToLastComment();
          });
        }
      });
    },
    watch: {
      // Watch for route parameter changes to handle navigation between different threads
      '$route.params.nid': {
        handler(newNid, oldNid) {
          if (newNid && newNid !== oldNid) {
            // Reset component state when navigating to a different thread
            this.commentsCurrentPage = 1;
            this.error = null;
            this.isFavorite = false;
            this.isNegative = false;
            this.userKarma = {};
            
            // Fetch the new thread data
            this.fetchThread().then(() => {
              // Check if we should scroll to last comment
              if (this.$route.query.last === 'true') {
                this.$nextTick(() => {
                  this.scrollToLastComment();
                });
              }
            });
          }
        },
        immediate: false
      },
      thread: {
        handler() {
          // Recalculate pagination when thread data changes
          //
        },
        deep: true
      },
      comments: {
        handler(newComments) {
          if (this.shouldScrollToLastComment && newComments.length > 0) {
            this.$nextTick(() => {
              this.scrollToLastComment();
              this.shouldScrollToLastComment = false;
            });
          }
        },
        deep: true
      }
    },
    methods: {
      fetchThread() {
        this.loading = true;
        const nid = this.$route.params.nid;
        
        return new Promise((resolve, reject) => {
          axios
            .get(`/forum/thread/${nid}`)
            .then(response => {
              this.thread = response.data.thread;
              this.comments = response.data.comments;
              
              // Verificar si es un hilo de tipo stock (sin autor o author_uid)
              //
              
              // Force Vue to recalculate computed values after data is loaded
              this.$nextTick(() => {
                //
              });
              
              // After loading thread data, fetch karma for all users
              this.fetchUserKarma();
              
              // Check if thread is favorited after loading thread data
              if (this.isLoggedIn) {
                this.checkFavoriteStatus();
                
                // If user is admin, also check negative forum status
                if (this.isAdmin) {
                  this.checkNegativeForumStatus();
                }
              }

              // Make a background request to mark the node as read
              axios.get(`/node/${nid}`)
                .then(() => {
                  //
                })
                .catch(err => {
                  // Log error but don't block UI
                  //
                });
              
              // Resolve the promise after all data is loaded
              resolve(response);
            })
            .catch(err => {
              this.error = err;
              //
              reject(err);
            })
            .finally(() => {
              this.loading = false;
            });
        });
      },
      
      // Add new method to fetch karma data for all users in the thread
      fetchUserKarma() {
        // Collect all unique user IDs from the thread and comments
        const userIds = new Set();
        
        // Add thread author if it exists and is not a stock thread
        if (!this.isStockThread && this.thread.author_uid) {
          userIds.add(parseInt(this.thread.author_uid));
        }
        
        // Add all comment authors
        this.comments.forEach(comment => {
          if (comment.uid) {
            userIds.add(parseInt(comment.uid));
          }
        });
        
        // If we have user IDs, fetch their karma data
        if (userIds.size > 0) {
          axios.post('/api/forum-karma-batch', {
            uids: Array.from(userIds)
          })
          .then(response => {
            if (response.data && response.data.karma_data) {
              this.userKarma = response.data.karma_data;
            }
          })
          .catch(error => {
            //
          });
        }
      },
      
      // Add a method to get karma for a specific user
      getUserKarma(uid) {
        return this.userKarma[uid] || null;
      },
      
      formatTimestamp(ts) {
        const date = new Date(ts * 1000);
        return date.toLocaleString();
      },
      goToPage(pageNum) {
        this.commentsCurrentPage = pageNum;
      },
      prevCommentPage() {
        if (this.commentsCurrentPage > 1) {
          this.commentsCurrentPage--;
        }
      },
      nextCommentPage() {
        if (this.commentsCurrentPage < this.totalCommentPages) {
          this.commentsCurrentPage++;
        }
      },
      // Devuelve el comentario padre si pid es distinto de "0"
      getParentComment(comment) {
        if (comment.pid === "0") return null;
        return this.comments.find(c => c.cid === comment.pid);
      },
      goBack() {
        this.$router.push({ name: 'NewForum' });
      },
      getCommentStartIndex() {
        // If we're on the first page, start from 0
        // Otherwise, calculate based on page number and items per page
        if (this.commentsCurrentPage === 1) {
          return 0;
        } else {
          return (this.commentsCurrentPage - 1) * this.commentsPerPage;
        }
      },
      scrollToComment(commentId) {
        const commentElement = document.getElementById(`comment-${commentId}`);
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Add a highlight effect
          commentElement.classList.add('highlight-comment');
          setTimeout(() => {
            commentElement.classList.remove('highlight-comment');
          }, 2000);
        }
      },
      scrollToLastComment() {        
        // Asegurarnos de estar en la última página
        if (this.commentsCurrentPage !== this.totalCommentPages) {
          this.commentsCurrentPage = this.totalCommentPages;
          // Esperar a que se actualice la vista antes de desplazarse
          this.$nextTick(() => {
            setTimeout(() => {
              this.performScrollToLastComment();
            }, 100); // Give more time for pagination to update
          });
        } else {
          this.performScrollToLastComment();
        }
      },
      // Método auxiliar para realizar el desplazamiento
      performScrollToLastComment() {
        
        // Esperar un momento para asegurar que el DOM se ha actualizado completamente
        setTimeout(() => {
          // Obtener el último comentario visible en la página actual
          const visibleComments = document.querySelectorAll('.comment-item');
          
          if (visibleComments.length > 0) {
            const lastVisibleComment = visibleComments[visibleComments.length - 1];
            
            // Desplazarse al último comentario
            lastVisibleComment.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Resaltar el comentario
            lastVisibleComment.classList.add('highlight-comment');
            setTimeout(() => {
              lastVisibleComment.classList.remove('highlight-comment');
            }, 2000);
          } else {
            // Fallback: scroll to bottom of page
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth'
            });
          }
          
          // Remove the "last" query parameter after scrolling
          this.removeLastQueryParameter();
        }, 500); // Increased wait time to ensure DOM is updated
      },
      // New methods for reply functionality
      openReplyModal() {
        if (!this.canWrite) {
          // Optional: display notification
          alert('No tienes permisos para escribir en el foro.');
          return;
        }
        this.quotedComment = null;
        this.quotedCommentNumber = null;
        this.showReplyModal = true;
      },
      
      closeReplyModal() {
        this.showReplyModal = false;
        this.quotedComment = null;
        this.quotedCommentNumber = null;
      },
      
      handleQuoteComment(comment, commentNumber) {
        this.quotedComment = comment;
        this.quotedCommentNumber = commentNumber;
        this.showReplyModal = true;
      },
      
      handleReplyToComment(comment, commentNumber) {
        this.quotedComment = comment;
        this.quotedCommentNumber = commentNumber;
        this.showReplyModal = true;
      },
      
      getCommentNumber(comment) {
        if (comment.cid === 'op') {
          return 1;
        }
        
        const index = this.sortedComments.findIndex(c => c.cid === comment.cid);
        if (index !== -1) {
          return index + 2; // +2 because index is 0-based and OP is #1
        }
        
        return null;
      },
      
      handleReplySubmitted(responseData) {
        // Refresh the thread to show the new comment
        this.fetchThread().then(() => {
          // After refreshing, navigate to the last page where the new comment should be
          this.commentsCurrentPage = this.totalCommentPages;
          
          // After the page is loaded and comments are rendered, scroll to the new comment
          this.$nextTick(() => {
            const newCommentId = responseData.cid;
            if (newCommentId) {
              this.scrollToComment(newCommentId);
            } else {
              // If we don't have the new comment ID, just scroll to the bottom
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth'
              });
            }
          });
        });
        
        // Emit event to update user activity widget
        this.$root.$emit('userActivityUpdated', 'comment');
      },
      // Add new methods for first and last page navigation
      goToFirstPage() {
        this.commentsCurrentPage = 1;
      },
      
      goToLastPage() {
        this.commentsCurrentPage = this.totalCommentPages;
      },
      toggleFavorite() {
        if (this.favoriteLoading) return;
        
        this.favoriteLoading = true;
        const threadId = this.$route.params.nid;
        
        // Use the new endpoint to toggle favorite status
        axios.post(`/forum/api/favorite/toggle/${threadId}`)
          .then(response => {
            if (response.data && response.data.success) {
              // Update the favorite status based on the response
              this.isFavorite = response.data.isFavorite;
              
              // Emit event to update user activity widget
              this.$root.$emit('userActivityUpdated', 'favorite');
            } else {
              throw new Error(response.data.error || 'Unknown error');
            }
          })
          .catch(err => {
            //
            // Show error message
            const errorMessage = 'Error al cambiar el estado de favorito';
            //
          })
          .finally(() => {
            this.favoriteLoading = false;
          });
      },
      checkFavoriteStatus() {
        // Use the new endpoint to check favorite status directly
        const threadId = this.$route.params.nid;
        axios.get(`/forum/api/favorite/check/${threadId}`)
          .then(response => {
            if (response.data && response.data.success) {
              this.isFavorite = response.data.isFavorite;
            }
          })
          .catch(err => {
            //
          });
      },
      // Add method to toggle negative forum flag
      toggleNegativeForum() {
        if (this.negativeForumLoading) return;
        
        this.negativeForumLoading = true;
        const threadId = this.$route.params.nid;
        
        // Use the endpoint to toggle negative_forum status
        axios.post(`/forum/api/flag/toggle/negative_forum/${threadId}`)
          .then(response => {
            if (response.data && response.data.success) {
              // Update the status based on the response
              this.isNegative = response.data.isFlagged;
              
              //
            } else {
              throw new Error(response.data.error || 'Unknown error');
            }
          })
          .catch(err => {
            //
            // Show error message
            const errorMessage = 'Error al cambiar el estado de visibilidad del hilo';
            //
          })
          .finally(() => {
            this.negativeForumLoading = false;
          });
      },
      
      // Add method to check negative forum status
      checkNegativeForumStatus() {
        // Check if the thread is marked as negative (not visible in recent content)
        if (!this.isAdmin) return; // Only check for admins
        
        const threadId = this.$route.params.nid;
        axios.get(`/forum/api/flag/check/negative_forum/${threadId}`)
          .then(response => {
            if (response.data && response.data.success) {
              this.isNegative = response.data.isNegative;
            }
          })
          .catch(err => {
            //
          });
      },
      
      handleNeedResponseChanged(data) {
        // Update the local data to reflect the change
        if (data.type === 'topic') {
          if (this.thread) {
            this.thread.needs_response = data.needsResponse;
          }
        } else if (data.type === 'comment') {
          const comment = this.comments.find(c => c.cid === data.id);
          if (comment) {
            if (!comment.field_need_response) {
              comment.field_need_response = { und: [{ value: '0' }] };
            }
            comment.field_need_response.und[0].value = data.needsResponse ? '1' : '0';
          }
        }
        
        // You could also refresh the thread data here if needed
      },
      
      handleOriginalPostUpdate(messageData) {
        // Update the thread body when the original post is edited or deleted
        if (messageData.type === 'success') {
          // Refresh the thread data to get the updated content
          this.fetchThread();
        }
      },
      
      scrollToBottom() {
        this.$nextTick(() => {
          setTimeout(() => {
            const lastElement = document.querySelector('.thread-pagination.bottom-pagination');
            if (lastElement) {
              lastElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
            } else {
              // Fallback for cases where the primary targets aren't available
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth'
              });
            }
          }, 100);
        });
      },
      scrollToTop() {
        this.$nextTick(() => {
          const topElement = document.querySelector('.thread-header');
          if (topElement) {
            topElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            // Fallback to just scrolling to the top
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        });
      },
      goToFirstMessage() {
        this.commentsCurrentPage = 1;
        this.$nextTick(() => {
          setTimeout(() => {
            // Scroll to the original post or stock thread header
            const firstElement = document.querySelector('.original-post, .stock-thread-header');
            if (firstElement) {
              firstElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              // Fallback to scrolling to top
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
        });
      },
      goToLastMessage() {
        this.commentsCurrentPage = this.totalCommentPages;
        this.$nextTick(() => {
          setTimeout(() => {
            // Try to find the last comment on the page
            const allComments = document.querySelectorAll('.comment-item');
            if (allComments.length > 0) {
              const lastComment = allComments[allComments.length - 1];
              lastComment.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              // Add highlight effect
              lastComment.classList.add('highlight-comment');
              setTimeout(() => {
                lastComment.classList.remove('highlight-comment');
              }, 2000);
            } else {
              // Fallback to scroll to bottom
              this.scrollToBottom();
            }
          }, 200);
        });
      },
      
      removeLastQueryParameter() {
        // Remove the "last" query parameter from the URL without triggering a navigation
        if (this.$route.query.last) {
          const newQuery = { ...this.$route.query };
          delete newQuery.last;
          
          // Use replace to avoid adding to browser history
          this.$router.replace({
            name: this.$route.name,
            params: this.$route.params,
            query: newQuery
          });
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .thread-wrapper {
    background-color: var(--color-background);
    min-height: 100vh;
    padding: 40px 20px;
  }
  
  .thread-layout {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    gap: 20px;
  }
  
  /* Main Column Styles - Updated to remove shared background */
  .main-column {
    flex: 3;
    color: var(--color-text-primary);
  }
  
  /* Thread Header Styles - Updated */
  .thread-header {
    margin-bottom: 20px;
    padding: 20px;
    background-color: var(--color-surface);
    border-radius: 15px 15px 0 0; /* Rounded only at the top */
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
  
  .thread-header h1 {
    margin: 0;
    text-align: center;
  }
  
  /* Thread Pagination Styles */
  .thread-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 15px;
    background-color: var(--color-surface);
    flex-direction: column;
    border-top: 1px solid var(--color-outline);
  }
  
  .pagination-nav-btn {
    background-color: var(--color-background);
    color: var(--color-text-primary);
    border: none;
    padding: 5px 10px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .pagination-nav-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination-nav-btn:hover:not(:disabled) {
    background-color: var(--color-highlight);
    color: var(--color-on-highlight, white);
  }
  
  .pagination-numbers {
    display: flex;
    gap: 5px;
    margin: 0 5px;
  }
  
  .pagination-number-btn {
    background-color: var(--color-background);
    color: var(--color-text-primary);
    border: none;
    min-width: 30px;
    height: 30px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .pagination-number-btn:hover {
    background-color: var(--color-highlight);
    color: var(--color-on-highlight, white);
  }
  
  .pagination-number-btn.active {
    background-color: var(--color-highlight);
    color: var(--color-on-highlight, white);
    font-weight: bold;
  }
  
  /* Thread Content Styles - Renamed from thread-body */
  .thread-content {
    margin: 20px 0;
  }
  
  /* Original Post Styles */
  .original-post {
    border-left: 3px solid var(--color-highlight);
  }
  
  /* Stock Thread Header Styles */
  .stock-thread-header {
    margin-bottom: 20px;
  }
  
  /* Loading and Error Messages */
  .loading-message,
  .error-message {
    padding: 20px;
    background-color: var(--color-surface);
    border-radius: 8px;
    text-align: center;
    margin: 20px 0;
  }
  
  .error-message {
    border-left: 4px solid #f44336;
  }
  
  /* Side Column Styles */
  .side-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  /* Pagination Styles */
  .comments-pagination {
    display: none;
  }
  
  /* Bottom pagination specific styles */
  .bottom-pagination {
    margin-top: 20px;
    border-radius: 0 0 15px 15px; /* Rounded only at the bottom */
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
  
  .pagination-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  .thread-navigation-row {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 10px;
  }

  .thread-nav-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .thread-nav-btn:hover:not(:disabled) {
    background-color: var(--color-active-surface);
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .thread-layout {
      flex-direction: column;
    }
    
    .main-column,
    .side-column {
      flex: 1;
      width: 100%;
    }
    
    .side-column {
      margin-top: 20px;
    }
  }
  
  /* List Reset */
  ul {
    padding: 0;
    margin: 0;
  }
  
  /* Add highlight effect for when a comment is referenced */
  .highlight-comment {
    animation: highlight-pulse 2s ease;
  }
  
  @keyframes highlight-pulse {
    0% { box-shadow: 0 0 0 0 rgba(var(--color-highlight-rgb, 33, 150, 243), 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(var(--color-highlight-rgb, 33, 150, 243), 0); }
    100% { box-shadow: 0 0 0 0 rgba(var(--color-highlight-rgb, 33, 150, 243), 0); }
  }
  
  /* Add styles for the thread reply button */
  .thread-reply-container {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
  
  .thread-reply-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: var(--color-highlight);
    color: var(--color-on-highlight, white);
    border: none;
    padding: 12px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.3s ease;
  }
  
  .thread-reply-btn:hover {
    background-color: var(--color-highlight-dark, #1976d2);
  }
  
  .thread-reply-btn i {
    font-size: 20px;
  }
  
  /* Favorite Button Styles */
  .thread-actions {
    display: flex;
    justify-content: center;
    margin: 10px 0;
    gap: 10px;
  }
  
  .favorite-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .favorite-btn:hover:not(:disabled) {
    background-color: var(--color-active-surface);
  }
  
  .favorite-btn.is-favorite {
    background-color: var(--color-highlight-light, #e3f2fd);
    color: var(--color-highlight);
    border-color: var(--color-highlight);
  }
  
  .favorite-btn.is-favorite i {
    color: #ffc107; /* Gold star color */
  }
  
  .favorite-btn:disabled {
    opacity: 0.7;
    cursor: wait;
  }
  
  /* Negative Forum Button Styles */
  .negative-forum-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .negative-forum-btn:hover:not(:disabled) {
    background-color: var(--color-active-surface);
  }
  
  .negative-forum-btn.is-negative {
    background-color: rgba(244, 67, 54, 0.1);
    color: #f44336;
    border-color: #f44336;
  }
  
  .negative-forum-btn.is-negative i {
    color: #f44336;
  }
  
  .negative-forum-btn:disabled {
    opacity: 0.7;
    cursor: wait;
  }
  
  /* Scroll to Bottom Button Styles */
  .scroll-to-bottom-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .scroll-to-bottom-btn:hover {
    background-color: var(--color-active-surface);
    color: var(--color-highlight);
  }
  
  .scroll-to-bottom-btn i {
    font-size: 20px;
  }
  .scroll-to-top-btn {
    margin-left: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .scroll-to-top-btn i {
    font-size: 20px;
  }
  
  /* Unified Page Navigation Button Styles */
  .page-navigation-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    padding: 10px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    font-weight: 500;
  }
  
  .page-navigation-btn:hover {
    background-color: var(--color-highlight);
    color: var(--color-on-highlight, white);
    border-color: var(--color-highlight);
  }
  
  .page-navigation-btn i {
    font-size: 18px;
  }
  
  /* Remove margin for bottom pagination page navigation button */
  .bottom-pagination .page-navigation-btn {
    margin-left: 0;
  }
  </style>
  